import React, { useCallback, useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { withTheme } from 'styled-components';
import { SwapIcon } from 'ggApp/shared/icons/Swap';

import Translator from 'ggApp/modules/translator/components/PureTranslator';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { CartItemModel } from 'ggApp/shared/context/Order/services/integration';
import { registerMixLpClick } from 'ggApp/modules/gaAnalytics/actions';
import { ContractState } from 'ggApp/types/graphql';
import CheckContracts from './contract.graphql';
import { MixCta } from './MixCta';

import {
    Container,
    Header,
    Description,
    CardCheck,
    HeaderContent,
    HeaderTitle,
    LabelNew,
    LearnMore,
    Content,
    Delivery,
    Images,
    Img,
    Icon,
    CardImage,
    Promo,
} from './styles';

// TODO: export from common (or move to common
interface ProductModel {
    image: string;
    categoryId: string;
    subCategoryId: string;
    productSku: string;
    variantSku: string;
}

interface User {
    id: string;
}

interface MixableProductCardProps {
    className?: string;
    active: boolean;
    theme: {
        colors: {
            Foreground: string;
            ['Level 2']: string;
            secondary: string;
        };
    };
    activeProduct: ProductModel;
    onCtaClick: () => void;
    selectFlow?: () => void;
    disabled?: boolean;
    inFlexCartFlow: boolean;
    deliveryEstimate?: { from: number; to: number };
}

interface LineItemModel {
    coreAttribute: string;
    id: string;
    imageUrl: string;
    name: string;
    quantity: number;
    variant: {
        colorCodes: [string];
        colorTitle: string;
        id: string;
        mixable: boolean;
        sku: string;
        product: {
            category: {
                categoryId: string;
                subCategoryId: string;
            };
            sku: string;
            slug: string;
        };
    };
}

const mapLineItem = (item: LineItemModel): ProductModel => {
    const { variant, imageUrl } = item;
    const { product, sku } = variant;
    return {
        categoryId: product?.category?.categoryId,
        subCategoryId: product?.category?.subCategoryId,
        variantSku: sku,
        productSku: product?.sku,
        image: imageUrl,
    };
};

export const MixableProductCard = withTheme(
    ({
        className,
        active,
        theme,
        activeProduct,
        onCtaClick,
        selectFlow,
        disabled,
        inFlexCartFlow,
        deliveryEstimate,
    }: MixableProductCardProps): JSX.Element => {
        const { cart = {}, user = {} } = useContext(OrderContext);
        const { items = [], orderNumber } = cart as CartItemModel;
        const { id: userId } = user as User;

        const products = useMemo(() => {
            return items.map(mapLineItem);
        }, [items]);

        const needPlaceholder: boolean = useMemo(() => {
            return Boolean(
                activeProduct &&
                    products.length < 3 &&
                    products.every((p: ProductModel) => p.productSku !== activeProduct?.productSku),
            );
        }, [activeProduct, products]);

        const EmptyCardCount = Math.max(3 - products.length - +needPlaceholder, 0);

        const contractsFilter = useMemo(
            () => ({
                userId,
                type: 'MIX',
                state: [ContractState.ACTIVE, ContractState.FULFILLING, ContractState.DRAFT],
            }),
            [userId],
        );

        const { data } = useQuery(CheckContracts, {
            variables: {
                contractsFilter,
                product: {
                    categoryId: activeProduct?.categoryId,
                    productSku: activeProduct?.productSku,
                    subCategoryId: activeProduct?.subCategoryId,
                    variantSku: activeProduct?.variantSku,
                },
            },
        });

        const contracts = data?.contracts?.nodes;

        const mixProducts = contracts && contracts[0]?.goods;

        const handleClickLearnMore = useCallback(() => {
            registerMixLpClick({
                from: 'pdp',
                orderID: orderNumber,
                userID: userId,
                sku: activeProduct?.productSku,
            });
        }, [orderNumber, userId, activeProduct]);

        const renderImages = useMemo(() => {
            if (mixProducts) {
                return (
                    <Images>
                        {mixProducts.map(
                            (p: any) =>
                                (p?.state === 'WITH_CUSTOMER' ||
                                    p?.state === 'AWAITING_RETURN' ||
                                    (p?.state === 'RESERVED' &&
                                        contracts[0]?.state === 'FULFILLING')) && (
                                    <Img>
                                        <CardImage
                                            key={p.id}
                                            src={p?.variant?.imageUrl}
                                            active={p?.swappable}
                                            inSwap={p?.state === 'AWAITING_RETURN'}
                                        />
                                        {p?.state === 'AWAITING_RETURN' && (
                                            <Icon>
                                                <SwapIcon />
                                            </Icon>
                                        )}
                                    </Img>
                                ),
                        )}
                    </Images>
                );
            }

            return (
                <Images>
                    {Array.isArray(products) &&
                        products.map((p) => (
                            <Img>
                                <CardImage active src={p.image} />
                            </Img>
                        ))}

                    {needPlaceholder ? (
                        <Img>
                            <CardImage isPreview src={(activeProduct as ProductModel).image} />
                        </Img>
                    ) : null}

                    {[...new Array(EmptyCardCount)].map((i) => (
                        <Img>
                            <CardImage isPreview key={i} />
                        </Img>
                    ))}
                </Images>
            );
        }, [contracts, products, mixProducts, EmptyCardCount, activeProduct, needPlaceholder]);

        return (
            <Container
                data-testid="mixable-card-id"
                className={className}
                onClick={!active ? selectFlow : undefined}
                active={active}
                disabled={disabled}
            >
                <Header>
                    <CardCheck
                        id="mixable-rental-radio"
                        name="pdp-rental-option-radio"
                        disabled={disabled}
                        dotColor={theme.colors.Foreground}
                        borderColor={theme.colors['Level 2']}
                        checked={active}
                    />

                    <HeaderContent>
                        <HeaderTitle>
                            <Translator tk="MIXABLE_RENTAL_PLAN_LABEL" />
                        </HeaderTitle>

                        {!active ? (
                            <Description>
                                <Translator tk="MIXABLE_CARD_OVERVIEW" />
                            </Description>
                        ) : null}
                    </HeaderContent>

                    {!active ? (
                        <LabelNew label="new" />
                    ) : (
                        <LearnMore to="/mix" onClick={handleClickLearnMore}>
                            <Translator tk="MIXABLE_LEARN_MORE_CTA" />
                        </LearnMore>
                    )}
                </Header>

                {active ? (
                    <Content>
                        {renderImages}

                        <Promo />

                        <MixCta
                            onCtaClick={onCtaClick}
                            products={products}
                            inFlexCartFlow={inFlexCartFlow}
                            contracts={contracts}
                            activeProduct={activeProduct}
                        />

                        <Delivery deliveryEstimation={deliveryEstimate} tinyText />
                    </Content>
                ) : null}
            </Container>
        );
    },
);
